import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useNavigate } from "react-router-dom";
import { License } from "../../store/types";
import { useProjectsAPI } from "../../hooks/api/use-projectsAPI";
import { useLicensesAPI } from "../../hooks/api/use-licensesAPI";
import { useCompaniesAPI } from "../../hooks/api/use-companiesAPI";
import { useUsersAPI } from "../../hooks/api/use-usersAPI";
import { useGetQuery } from "../../store/slices/apiSlice";
import { getLicenseType } from "../../hooks/useLicenseType";
import {
  Container,
  ContainerXScroll,
} from "../../components/Container/Container";
import Card from "../../components/Card/Card";
import CustomInputText from "../../components/CustomInput/CustomInputText";
import CustomDatatable from "../../components/CustomDatatable/CustomDatatable";
import {
  projectLicenseColumns,
  getLicenseContextMenuItems,
  getLicenseMobileActions,
} from "../../components/DatatableTemplates/LicenseTableTemplates";
import AddLicenseDialogButton from "../../components/DialogButton/AddLicenseDialogButton";
import ProjectDialogs from "../../components/Dialogs/ProjectDialogs";
import LicenseDialogs from "../../components/Dialogs/LicenseDialogs";
import { StyledIconLine } from "../../components/StyledIcon/StyledIcon";
import { H2 } from "../../styles/Typography";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import {
  IconDocFilled,
  IconDocCheckFilled,
  IconDocWarnFilled,
  IconDocErrorFilled,
  IconArrowLeft,
  IconSearch,
  IconCaseFilled,
  IconEdit,
  IconDelete,
} from "../../assets/icons/Icon";
import {
  ContainerFlex,
  ContainerColumnFlex,
  CardsColumnContainer,
  FlexColumn,
  CardsFlex,
  ButtonContainer,
  ButtonLabel,
  SearchContainer,
} from "./ProjectDetails.styles";
import {
  FlexLineJustify,
  FlexLineGapSmall,
  PageName,
  Label,
  ButtonBox,
} from "../../styles/PageLayoutStyles.styles";
import { Spinner } from "../CRUDPages/CRUDPages.styles";

const ProjectDetails: React.FC = () => {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)") || false;

  const tableRef = useRef<DataTable<License[]>>(null);
  const [licenseSearchValue, setLicenseSearchValue] = useState<string>("");
  const [licenseList, setLiscenseList] = useState<License[]>([]);
  const [totalLicenses, setTotalLicenses] = useState(0);
  const [activeLicenses, setActiveLicenses] = useState(0);
  const [expiringLicenses, setExpiringLicenses] = useState(0);
  const [inactiveLicenses, setInactiveLicenses] = useState(0);
  const [selectedLicense, setSelectedLicense] = useState<License | null>(null);
  const [projectDialogVisible, setProjectDialogVisible] =
    useState<boolean>(false);
  const [projectDialogType, setProjectDialogType] = useState<string>("");
  const [licenseDialogVisible, setLicenseDialogVisible] =
    useState<boolean>(false);
  const [licenseDialogType, setLicenseDialogType] = useState<string>("");

  const {
    projectsEP,
    handleUpdateProject,
    handleDeleteProject,
    toastRef: projectToastRef,
    refetchData,
  } = useProjectsAPI();
  const { companies, companiesDropdownOpts } = useCompaniesAPI();
  const {
    licensesEP,
    handlePostLicense,
    handleActivateLicense,
    handleDeactivateLicense,
    handleUpdateLicense,
    handleUpdateLicenseUser,
    handleDeleteLicense,
    toastRef,
  } = useLicensesAPI();
  const { users } = useUsersAPI();

  const {
    data: project,
    refetch: refetchProject,
    isLoading,
  } = useGetQuery(`${projectsEP}/${projectId}`, {
    refetchOnMountOrArgChange: true,
    skip: !projectId || !projectsEP,
  });

  const {
    data: licenses,
    refetch: refetchLicenses,
    isLoading: isLicensesLoading,
  } = useGetQuery(`${projectsEP}/${projectId}/licenses`, {
    refetchOnMountOrArgChange: true,
    skip: !projectId || !projectsEP,
  });

  useEffect(() => {
    if (project && licenses) {
      const enriched = licenses.map((license: License) => {
        const userName = license.userShortName ?? "—";
        const licenseType = getLicenseType(license.type);
        const companyName =
          companies?.find((p) => p.id === license.organizationId)?.name ??
          license.organizationId;

        let validityDays = 0;
        if (license.startDate && license.expirationDate) {
          const start = new Date(license.startDate);
          const end = new Date(license.expirationDate);
          validityDays = Math.max(
            0,
            (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
          );
        }

        let remainingDays = 0;
        if (license.expirationDate) {
          const now = new Date();
          const exp = new Date(license.expirationDate);
          remainingDays =
            (exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
        }

        return {
          ...license,
          userName,
          companyName,
          licenseType,
          _validityDays: validityDays,
          _timeRemainingDays: remainingDays,
        };
      });
      setLiscenseList(enriched);

      const now = new Date();
      let total = licenses.length;
      let active = 0;
      let expiring = 0;
      let inactive = 0;

      licenses.forEach((license: License) => {
        const isActive = license.isEnabled;
        const expDate = license.expirationDate
          ? new Date(license.expirationDate)
          : null;
        const isExpired = expDate && expDate < now;
        const daysRemaining = expDate
          ? (expDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
          : Infinity;

        if (isActive && !isExpired) {
          active++;
          if (daysRemaining < 30) expiring++;
        } else {
          inactive++;
        }
      });

      setTotalLicenses(total);
      setActiveLicenses(active);
      setExpiringLicenses(expiring);
      setInactiveLicenses(inactive);
    } else {
      setTotalLicenses(0);
      setActiveLicenses(0);
      setExpiringLicenses(0);
      setInactiveLicenses(0);
    }
  }, [project, licenses]);

  const filteredData = useMemo(() => {
    if (!licenseSearchValue.trim() || !licenseList) return licenseList;
    const lowerSearch = licenseSearchValue.toLowerCase();
    return licenseList.filter((license) => {
      const lic = license as any;
      return (
        String(lic.companyName ?? "")
          .toLowerCase()
          .includes(lowerSearch) ||
        String(lic.userName ?? "")
          .toLowerCase()
          .includes(lowerSearch) ||
        String(lic.licenseType ?? "")
          .toLowerCase()
          .includes(lowerSearch) ||
        String(lic.isEnabled ?? "")
          .toLowerCase()
          .includes(lowerSearch) ||
        String(lic.startDate ?? "")
          .toLowerCase()
          .includes(lowerSearch) ||
        String(lic.expirationDate ?? "")
          .toLowerCase()
          .includes(lowerSearch) ||
        String(lic.validityPeriod ?? "")
          .toLowerCase()
          .includes(lowerSearch) ||
        String(lic.timeRemaining ?? "")
          .toLowerCase()
          .includes(lowerSearch)
      );
    });
  }, [licenseList, licenseSearchValue]);

  const handleEnableLicense = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=enable&id=${license.id}`);
    } else {
      setLicenseDialogType("enable");
      setLicenseDialogVisible(true);
    }
  };

  const handleDisableLicense = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=disable&id=${license.id}`);
    } else {
      setLicenseDialogType("disable");
      setLicenseDialogVisible(true);
    }
  };

  const handleAddUser = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=addUser&id=${license.id}`);
    } else {
      setLicenseDialogType("addUser");
      setLicenseDialogVisible(true);
    }
  };

  const contextMenuItems = useMemo(
    () =>
      getLicenseContextMenuItems(
        selectedLicense,
        handleAddUser,
        handleEnableLicense,
        handleDisableLicense
      ),
    [selectedLicense, handleAddUser, handleEnableLicense, handleDisableLicense]
  );

  const mobileActions = useMemo(
    () => getLicenseMobileActions(handleEnableLicense, handleDisableLicense),
    [handleEnableLicense, handleDisableLicense]
  );

  const handleViewLicense = (license: License) => {
    navigate(`/licenses/${license.id}`);
  };

  const handleSelectionChange = (selected: License | null) => {
    setSelectedLicense(selected);
  };

  const resetAllFilters = () => {
    if (tableRef.current) {
      tableRef.current.reset();
    }
    setLicenseSearchValue("");
  };

  const editLicense = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=edit&id=${license.id}`);
    } else {
      setLicenseDialogType("edit");
      setLicenseDialogVisible(true);
    }
  };

  const deleteLicense = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=delete&id=${license.id}`);
    } else {
      setLicenseDialogType("delete");
      setLicenseDialogVisible(true);
    }
  };

  const editProject = () => {
    if (isMobile) {
      navigate(`/projects/form?mode=edit&id=${project.id}`);
    } else {
      setProjectDialogType("edit");
      setProjectDialogVisible(true);
    }
  };

  const deleteProject = () => {
    if (isMobile) {
      navigate(`/projects/form?mode=delete&id=${project.id}`);
    } else {
      setProjectDialogType("delete");
      setProjectDialogVisible(true);
    }
  };

  const deleteProjectAPI = async (): Promise<boolean> => {
    try {
      let success = await handleDeleteProject(project);
      if (success) {
        return true;
      }
      return false;
    } catch (error) {
      return false;
    } finally {
      setTimeout(() => {
        navigate(-1);
      }, 2000);
      refetchData();
    }
  };

  if (isLoading || !project)
    return (
      <Container>
        <Spinner>
          <ProgressSpinner />
          <p>Загрузка...</p>
        </Spinner>
      </Container>
    );

  return (
    <ContainerFlex>
      <Toast ref={toastRef} position="bottom-center" />
      <Toast ref={projectToastRef} position="bottom-center" />
      <ContainerColumnFlex>
        <CardsColumnContainer>
          <Container>
            <FlexLineJustify>
              <H2>Данные проекта</H2>
              <ButtonBox>
                <Button text onClick={editProject}>
                  <IconEdit size={16} />
                </Button>
                <Button text onClick={deleteProject}>
                  <IconDelete size={16} />
                </Button>
              </ButtonBox>
            </FlexLineJustify>
            <StyledIconLine
              icon={<IconCaseFilled size={20} />}
              text={project ? `${project.name}` : ""}
            />
          </Container>

          <Container>
            <H2>Статистика</H2>
            <FlexColumn>
              <CardsFlex>
                <Card
                  icon={<IconDocFilled size={24} />}
                  header={totalLicenses.toString()}
                  title="Количество лицензий"
                />
                <Card
                  icon={<IconDocCheckFilled size={24} />}
                  header={activeLicenses.toString()}
                  title="Активные лицензии"
                />
              </CardsFlex>
              <CardsFlex>
                <Card
                  icon={<IconDocWarnFilled size={24} />}
                  header={expiringLicenses.toString()}
                  title="Истекающие лицензии"
                />
                <Card
                  icon={<IconDocErrorFilled size={24} />}
                  header={inactiveLicenses.toString()}
                  title="Неактивные лицензии"
                />
              </CardsFlex>
            </FlexColumn>
          </Container>
        </CardsColumnContainer>

        <ButtonContainer>
          <Button outlined onClick={() => navigate(-1)}>
            <IconArrowLeft size={16} />
            <ButtonLabel>Назад</ButtonLabel>
          </Button>
        </ButtonContainer>
      </ContainerColumnFlex>

      <ContainerXScroll>
        <FlexLineJustify>
          <PageName>
            <H2>Лицензии</H2>
          </PageName>
          <FlexLineGapSmall>
            <AddLicenseDialogButton
              project={project}
              companiesOptions={companiesDropdownOpts}
              handleActivateLicense={handleActivateLicense}
              handleDeactivateLicense={handleDeactivateLicense}
              handleAddLicense={handlePostLicense}
              handleEditLicense={handleUpdateLicense}
              handleEditLicenseUser={handleUpdateLicenseUser}
              handleDeleteLicense={handleDeleteLicense}
              refetchData={refetchLicenses}
              licensesEP={licensesEP}
              isMobile={isMobile}
            />
            <Button
              icon="pi pi-filter-slash"
              onClick={resetAllFilters}
              outlined
            >
              <Label>Сбросить</Label>
            </Button>
            <SearchContainer>
              <CustomInputText
                value={licenseSearchValue}
                setValue={setLicenseSearchValue}
                placeholder="Поиск..."
                icon={<IconSearch size={20} />}
              />
            </SearchContainer>
          </FlexLineGapSmall>
        </FlexLineJustify>
        <CustomDatatable<License>
          ref={tableRef}
          data={filteredData ?? []}
          columns={projectLicenseColumns}
          onView={handleViewLicense}
          onEdit={editLicense}
          onDelete={deleteLicense}
          onRowDoubleClick={handleViewLicense}
          selected={selectedLicense}
          onSelectionChange={handleSelectionChange}
          getRowId={(license) => license.id}
          extraContextMenuItems={contextMenuItems}
          hideViewButton={false}
          mobileActions={mobileActions}
          sortable
        />
        <LicenseDialogs
          visible={licenseDialogVisible}
          onHide={() => setLicenseDialogVisible(false)}
          dialogType={licenseDialogType}
          license={selectedLicense!}
          enrichedLicense={selectedLicense!} // ??
          project={project}
          handleActivateLicense={handleActivateLicense}
          handleDeactivateLicense={handleDeactivateLicense}
          handleAddLicense={handlePostLicense}
          handleEditLicense={handleUpdateLicense}
          handleEditLicenseUser={handleUpdateLicenseUser}
          handleDeleteLicense={handleDeleteLicense}
          refetchData={refetchLicenses}
          companiesOptions={companiesDropdownOpts}
          usersList={users}
          licensesEP={licensesEP}
        />
      </ContainerXScroll>

      <ProjectDialogs
        visible={projectDialogVisible}
        onHide={() => setProjectDialogVisible(false)}
        dialogType={projectDialogType}
        project={project}
        handleEditProject={handleUpdateProject}
        handleDeleteProject={deleteProjectAPI}
        refetchData={refetchProject}
      />
    </ContainerFlex>
  );
};

export default ProjectDetails;
